import React, { useState, useEffect } from 'react';
import { Plus, Calendar } from 'lucide-react';
import { doctorProgressNotesService, DoctorProgressNote } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface DoctorProgressTabProps {
  admissionId: string;
}

export default function DoctorProgressTab({ admissionId }: DoctorProgressTabProps) {
  const [notes, setNotes] = useState<DoctorProgressNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    diagnosis: string;
    plan: string;
    remarks: string;
  }>();

  useEffect(() => {
    loadNotes();
  }, [admissionId]);

  const loadNotes = async () => {
    try {
      const data = await doctorProgressNotesService.getByAdmission(admissionId);
      setNotes(data);
    } catch (error) {
      console.error('Failed to load progress notes:', error);
      toast.error('Failed to load progress notes');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: { diagnosis: string; plan: string; remarks: string }) => {
    try {
      await doctorProgressNotesService.create({
        admission_id: admissionId,
        note_date: new Date().toISOString(),
        diagnosis: data.diagnosis,
        plan: data.plan,
        remarks: data.remarks,
        doctor_id: '',
      });
      toast.success('Progress note added successfully');
      reset();
      setShowAddForm(false);
      loadNotes();
    } catch (error) {
      console.error('Failed to save progress note:', error);
      toast.error('Failed to save progress note');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Doctor Progress Notes</h3>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Progress Note
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Diagnosis
            </label>
            <textarea
              {...register('diagnosis')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Current diagnosis..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan
            </label>
            <textarea
              {...register('plan')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Treatment plan..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              {...register('remarks')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Additional remarks..."
            />
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Progress Note
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {notes.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No progress notes recorded yet</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {new Date(note.note_date).toLocaleString()}
                </span>
                <span className="text-xs text-gray-500">By: {note.doctor?.email}</span>
              </div>
              
              {note.diagnosis && (
                <div className="mb-2">
                  <p className="text-sm font-medium text-gray-700">Diagnosis:</p>
                  <p className="text-gray-800">{note.diagnosis}</p>
                </div>
              )}
              
              {note.plan && (
                <div className="mb-2">
                  <p className="text-sm font-medium text-gray-700">Plan:</p>
                  <p className="text-gray-800">{note.plan}</p>
                </div>
              )}
              
              {note.remarks && (
                <div>
                  <p className="text-sm font-medium text-gray-700">Remarks:</p>
                  <p className="text-gray-800">{note.remarks}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
